import React, { useEffect, useRef, useState } from "react";
import { GripVertical, Pin, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Member's own Today layout: drag boxes to reorder, pin favourites to the top; saved to the account
export const useTodayLayout = () => {
  const [lay, setLay] = useState({ order: [], pinned: [] });
  useEffect(() => { api.get("/prefs/today-layout").then((r) => setLay(r.data)).catch(() => {}); }, []);
  const save = (next) => {
    setLay(next);
    api.put("/prefs/today-layout", next).catch(() => toast.error("Could not save your layout"));
  };
  return { lay, save };
};

const arrange = (items, { order, pinned }) => {
  const rank = (id) => { const i = order.indexOf(id); return i < 0 ? 1000 + items.findIndex((x) => x.id === id) : i; };
  const rest = items.filter((x) => !pinned.includes(x.id)).sort((a, b) => rank(a.id) - rank(b.id));
  const pins = pinned.map((id) => items.find((x) => x.id === id)).filter(Boolean);
  return [...pins, ...rest];
};

export const ArrangeGrid = ({ children, lay, save, className, pinClassName }) => {
  const items = React.Children.toArray(children).filter(React.isValidElement).map((c) => ({ id: c.props.boxId || c.props.testid, node: c }));
  const list = arrange(items, lay);
  const drag = useRef(null);
  const [over, setOver] = useState(null);
  const ids = list.map((x) => x.id);
  const drop = (target) => {
    const src = drag.current;
    setOver(null);
    if (!src || src === target) return;
    const next = ids.filter((x) => x !== src);
    next.splice(next.indexOf(target), 0, src);
    const tp = lay.pinned.includes(target);
    save({ order: next, pinned: next.filter((x) => (x === src ? tp : lay.pinned.includes(x))) });
  };
  const togglePin = (id) => {
    const pinned = lay.pinned.includes(id) ? lay.pinned.filter((x) => x !== id) : [...lay.pinned, id];
    save({ order: ids, pinned });
    toast.success(lay.pinned.includes(id) ? "Unpinned" : "Pinned to the top");
  };
  const box = ({ id, node }) => {
    const pinned = lay.pinned.includes(id);
    return (
      <div key={id} className={`relative group/box transition-[opacity,transform] ${over === id ? "ring-2 ring-blue-400 rounded-2xl" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setOver(id); }} onDragLeave={() => setOver((o) => (o === id ? null : o))} onDrop={(e) => { e.preventDefault(); drop(id); }}
        data-testid={`box-${id}`}>
        <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-md bg-white/90 backdrop-blur px-0.5 opacity-60 group-hover/box:opacity-100 transition-opacity">
          <button onClick={() => togglePin(id)} title={pinned ? "Unpin" : "Pin to top"} data-testid={`box-pin-${id}`}
            className={`p-1 rounded hover:bg-slate-100 ${pinned ? "text-amber-500" : "text-slate-400"}`}>
            <Pin className="h-3.5 w-3.5" fill={pinned ? "currentColor" : "none"} />
          </button>
          <span draggable onDragStart={(e) => { drag.current = id; e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { drag.current = null; setOver(null); }}
            title="Drag to move" className="p-1 rounded cursor-grab active:cursor-grabbing text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`box-drag-${id}`}>
            <GripVertical className="h-3.5 w-3.5" />
          </span>
        </div>
        {node}
      </div>
    );
  };
  const pins = list.filter((x) => lay.pinned.includes(x.id));
  return (
    <>
      {pins.length > 0 && (
        <div data-testid="pinned-row">
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-mono tracking-widest text-amber-600"><Pin className="h-3 w-3" fill="currentColor" />PINNED</div>
          <div className={pinClassName}>{pins.map(box)}</div>
        </div>
      )}
      <div className={className} data-testid="today-grid">{list.filter((x) => !lay.pinned.includes(x.id)).map(box)}</div>
    </>
  );
};

export const ResetLayout = ({ save, lay }) => (lay.order.length || lay.pinned.length ? (
  <button onClick={() => { save({ order: [], pinned: [] }); toast.success("Layout reset"); }} data-testid="layout-reset"
    className="flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-700 transition-colors">
    <RotateCcw className="h-3 w-3" /> Reset layout
  </button>
) : null);
